import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import Tooltip, { tooltipClasses, type TooltipProps } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";

const ReviewerApprovalTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backdropFilter: "blur(10px) brightness(105%)",
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: "none",
    color: theme.palette.text.primary,
    fontSize: "0.75rem",
    lineHeight: 1.35,
    maxWidth: theme.spacing(42),
    padding: theme.spacing(1.25),
  },
}));

const ReviewerApprovalTooltipContent = styled("span")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.5),
}));

const ReviewerApprovalChips = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
}));

const ReviewerApprovalChip = styled("span", {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: "approved" | "pending" }>(({ status, theme }) => ({
  alignItems: "center",
  color: status === "approved" ? theme.palette.success.main : theme.palette.warning.main,
  display: "inline-flex",
  fontSize: "0.875rem",
  gap: theme.spacing(0.33),
  lineHeight: 1,
  padding: theme.spacing(0, 0.5),
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
  ".Mui-disabled &": {
    color: theme.palette.action.disabled,
  },
}));

const ReviewerApprovalIconSlot = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
  lineHeight: 0,
}));

export default function ReviewerApprovalBadge({
  invisible,
  pendingReviewCount,
}: Readonly<{
  invisible?: boolean;
  pendingReviewCount: number;
}>) {
  if (invisible) return null;

  const allReviewersApproved = pendingReviewCount === 0;
  const tooltipTitle = (
    <ReviewerApprovalTooltipContent>
      <Typography variant="subtitle2">Reviewer approvals</Typography>
      <span>
        {pendingReviewCount} {pluralize("reviewer", pendingReviewCount)} pending.
      </span>
      <span>
        {allReviewersApproved
          ? "All assigned reviewers have approved this proposal."
          : "Some reviewers have not yet approved this proposal."}
      </span>
    </ReviewerApprovalTooltipContent>
  );

  // The badge is only rendered when the proposal has at least one reviewer
  // (callers pass `invisible` otherwise), so a pending count of zero means
  // every assigned reviewer has finalized their review.
  return (
    <ReviewerApprovalTooltip placement="bottom" title={tooltipTitle}>
      <ReviewerApprovalChips
        aria-label={
          allReviewersApproved
            ? "All reviews complete"
            : `${pendingReviewCount} ${pluralize("review", pendingReviewCount)} pending`
        }
      >
        <ReviewerApprovalChip status={allReviewersApproved ? "approved" : "pending"} aria-hidden="true">
          <ReviewerApprovalIconSlot>{allReviewersApproved ? <CheckCircleIcon /> : <PendingIcon />}</ReviewerApprovalIconSlot>
          {!allReviewersApproved && <span>{pendingReviewCount}</span>}
        </ReviewerApprovalChip>
      </ReviewerApprovalChips>
    </ReviewerApprovalTooltip>
  );
}

function pluralize(label: string, count: number) {
  return count === 1 ? label : `${label}s`;
}
